import React, { useState } from 'react'
import {
  AddItemToBlacklistInput,
  ItemBlacklistEntry,
  Chef,
  CateringChefMenuItem,
} from '@types'
import { AutocompleteInput } from '@containers/common/form'
import Loader from '@components/common/Loader'
import { MdOutlineEdit } from 'react-icons/md'
import moment from 'moment'
import { disabledClasses } from '@components/groupOrders/UniversalMenuExplorer/utils'

type BaseProps = {
  list: ItemBlacklistEntry[]
  editable?: boolean
  close: () => void
}
type EditableProps = BaseProps & {
  editable?: true
  onChange: (updatedList: ItemBlacklistEntry[]) => void
  addItemToBlacklist: (
    input: AddItemToBlacklistInput,
  ) => Promise<ItemBlacklistEntry>
  updateItemBlacklistEntry: (input: {
    itemId: string
    active: boolean
    reason: string
  }) => Promise<ItemBlacklistEntry>
  loadChefs: (filters: { search?: string; status: 'active' }) => Promise<void>
  loadMenuItems: (filters: Record<string, any>) => Promise<void>
}

type ReadonlyProps = BaseProps & {
  editable: false
  user?: never
  addItemToBlacklist?: never
  updateItemBlacklistEntry?: never
  onChange?: never
  loadChefs?: never
  loadMenuItems?: never
}

type MenuItemBlacklistProps = EditableProps | ReadonlyProps

const MenuItemBlacklist: React.FC<MenuItemBlacklistProps> = ({
  editable,
  list,
  addItemToBlacklist,
  loadChefs,
  loadMenuItems,
  onChange,
  updateItemBlacklistEntry,
  close,
}) => {
  const [currentList, setCurrentList] = useState(list)
  const [newSelected, toggleNewSelected] = useState(editable)
  const [selectedVendor, setSelectedVendor] = useState<Chef | undefined>()
  const [selectedMenuItem, setSelectedMenuItem] = useState<
    CateringChefMenuItem | undefined
  >()
  const [blacklistReason, setBlacklistReason] = useState('')
  const [edittingListMap, setEdittingListMap] = useState<
    Record<string, string>
  >({})

  const [isLoading, setIsLoading] = useState(false)

  const excludedItemIds = currentList.map((e) => e.itemId)

  const buildSearchFilters = (
    search: Record<string, any>,
  ): Record<string, any> => {
    return {
      ...search,
      chefIds: [selectedVendor?.id],
      marketTypes: ['Group Order'],
      chefStatus: '',
      isApproved: 'true',
      excludedItemIds,
    }
  }

  const handleAddToList = async () => {
    if (!editable || !selectedMenuItem || !selectedVendor || !blacklistReason) {
      return
    }
    setIsLoading(true)
    const itemEntry = await addItemToBlacklist({
      itemId: selectedMenuItem.id,
      itemName: selectedMenuItem.name,
      chefId: selectedVendor.id,
      chefName: selectedVendor.name,
      reason: blacklistReason,
    })
    if (itemEntry) {
      const nextList = [...currentList, itemEntry]
      setCurrentList(nextList)
      onChange(nextList)
      setSelectedVendor(undefined)
      setSelectedMenuItem(undefined)
      setBlacklistReason('')
      toggleNewSelected(false)
    }
    setIsLoading(false)
  }

  const onCancelEditAudit = (entry: ItemBlacklistEntry) => {
    if (!editable) {
      return
    }
    const nextMap = { ...edittingListMap }
    delete nextMap[entry.itemId]

    setEdittingListMap(nextMap)
  }

  const onUpdateBlacklistEntry = async (entry: ItemBlacklistEntry) => {
    if (!editable) {
      return
    }
    setIsLoading(true)
    const nextReason = edittingListMap[entry.itemId] || ''
    const updatedEntry = await updateItemBlacklistEntry({
      itemId: entry.itemId,
      reason: nextReason,
      active: !entry.active,
    })
    if (updatedEntry) {
      const nextList = [...currentList].map((e) =>
        e.itemId == updatedEntry.itemId ? updatedEntry : e,
      )
      setCurrentList(nextList)
      onChange(nextList)
      onCancelEditAudit(entry)
    }
    setIsLoading(false)
  }

  const renderPrevAuditChanges = (entry: ItemBlacklistEntry) => {
    return entry.audits.map((audit, i) => {
      return (
        <div
          className="flex flex-row w-full items-center justify-between"
          key={i}
        >
          <p>
            {audit.active ? 'Activated' : 'Deactivated'} on{' '}
            {moment(audit.updatedAt).format('MM/DD/YY H:mm a')} by{' '}
            {audit.updatedBy}
            {': '}
            <span className="font-bold">{audit.reason}</span>
          </p>
        </div>
      )
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-40 w-full" style={{ height: '650px ' }}>
        <Loader />
      </div>
    )
  }

  return (
    <div className="min-h-40 w-full" style={{ height: '650px ' }}>
      <div className="flex flex-row items-center justify-around space-x-2 pb-2">
        <button
          className={disabledClasses(
            !editable,
            `${
              newSelected
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-solid border-gray-300'
            } font-semibold hover:cursor-pointer hover:bg-green-600 hover:text-white w-1/2 rounded-md h-10`,
            'opacity-40 cursor-not-allowed hover:bg-white hover:text-gray-700',
          )}
          disabled={!editable}
          onClick={() => toggleNewSelected(true)}
        >
          Add New +
        </button>
        <button
          className={`${
            !newSelected
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-700 border border-solid border-gray-300'
          } font-semibold hover:cursor-pointer hover:bg-green-600 hover:text-white w-1/2 rounded-md h-10`}
          onClick={() => toggleNewSelected(false)}
        >
          Manage Existing
        </button>
      </div>
      {newSelected ? (
        <div className="h-400 min-h-45 flex flex-col items-start justify-start w-full border-solid border-gray-600 bg-gray-300 rounded-md pl-3">
          <div className="w-full py-3 rounded-tr-md rounded-tl-md">
            {loadChefs && editable ? (
              <AutocompleteInput
                label="Search Vendors"
                width="45%"
                displayAttribute="name"
                loaderFunction={(search) =>
                  loadChefs({ ...search, status: 'active' })
                }
                onSelect={(vendor) => setSelectedVendor(vendor)}
              />
            ) : (
              <div className="px-4 py-2 text-sm text-gray-700">Read-only</div>
            )}
          </div>
          {selectedVendor && editable && (
            <div className="w-full py-3 rounded-tr-md rounded-tl-md">
              <AutocompleteInput
                label="Add Menu Item To Blacklist"
                width="45%"
                displayAttribute="name"
                loaderFunction={(search) =>
                  loadMenuItems(buildSearchFilters(search))
                }
                onSelect={(menuItem) => setSelectedMenuItem(menuItem)}
              />
            </div>
          )}
          {selectedMenuItem && editable && (
            <div className="w-full flex flex-col items-start justify-center border-gray-400 border-solid py-2">
              <label className="text-gray-label font-bold uppercase text-13px">
                Blacklist Reason
              </label>
              <input
                type="text"
                placeholder="Enter reason for blacklisting this item..."
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                className="w-1/2 h-10 rounded-md pl-3"
              />
            </div>
          )}
          <div className="flex flex-col items-center w-full py-5">
            <button
              className={disabledClasses(
                !editable || !blacklistReason,
                'w-3/4 rounded-md bg-green-600 text-white font-bold h-10',
                'opacity-25 cursor-not-allowed',
              )}
              disabled={!editable || !blacklistReason}
              onClick={handleAddToList}
            >
              Add to Blacklist
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-200 rounded-lg h-500">
          <label className="text-gray-label uppercase font-bold pl-6 py-3">
            Existing Blacklisted Menu Items
          </label>
          <div className="h-400 rounded-lg border border-solid border-gray-400 mx-6 mb-5">
            <div className="h-full w-full overflow-x-auto overflow-y-auto">
              <table className="w-full h-full">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-400">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-400">
                      Menu Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-400">
                      Latest Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-400">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentList.map((entry) => {
                    const isEditting =
                      edittingListMap[entry.itemId] !== undefined

                    return (
                      <tr
                        key={entry.itemId}
                        className="border-t-2 border-gray-400 border-solid"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {entry.chefName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {entry.itemName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {isEditting ? (
                            <div className="flex flex-col justify-evenly">
                              {renderPrevAuditChanges(entry)}
                              <input
                                type="text"
                                disabled={!editable}
                                className={disabledClasses(
                                  !editable,
                                  'px-2 py-2 rounded-md bg-white w-full border border-solid border-gray-400',
                                  'opacity-50 cursor-not-allowed',
                                )}
                                onChange={(e) =>
                                  setEdittingListMap({
                                    ...edittingListMap,
                                    [entry.itemId]: e.target.value,
                                  })
                                }
                              />
                              <button
                                className={disabledClasses(
                                  !editable,
                                  'w-32 mt-3 px-2 py-2 rounded-lg font-semibold bg-white border-solid border-gray-300 hover:cursor-pointer',
                                  'opacity-50 cursor-not-allowed',
                                )}
                                disabled={!editable}
                                onClick={() => onCancelEditAudit(entry)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            entry.reason
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {isEditting ? (
                            <div>
                              <button
                                onClick={() => onUpdateBlacklistEntry(entry)}
                                disabled={
                                  !editable || !edittingListMap[entry.itemId]
                                }
                                className={disabledClasses(
                                  !editable || !edittingListMap[entry.itemId],
                                  `${
                                    !edittingListMap[entry.itemId]
                                      ? 'bg-red-700'
                                      : 'bg-green-600'
                                  } text-white px-2 py-2 rounded-full font-bold hover:cursor-pointer`,
                                  'opacity-50 cursor-not-allowed',
                                )}
                              >
                                {entry.active ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                className={disabledClasses(
                                  !editable,
                                  `flex items-center justify-evenly px-2 py-2 rounded-full text-white font-bold ${
                                    entry.active ? 'bg-red-700' : 'bg-green-600'
                                  } hover:cursor-pointer`,
                                  'opacity-50 cursor-not-allowed',
                                )}
                                disabled={!editable}
                                onClick={() =>
                                  setEdittingListMap({
                                    ...edittingListMap,
                                    [entry.itemId]: '',
                                  })
                                }
                              >
                                {entry.active ? 'Blacklisted' : 'Inactive'}
                                <MdOutlineEdit />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col w-full items-center py-10">
        <button
          className="bg-green-600 rounded-md font-bold text-white h-10 w-1/3"
          onClick={close}
        >
          Done
        </button>
      </div>
    </div>
  )
}

export default MenuItemBlacklist
